<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import SectorsService from '@/api/services/sectors.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { ISector } from '@/interfaces/ISector';
  import { i18n } from '@/plugins/i18n';

  const props = defineProps<{
    restaurantId: number;
    branchId: number;
    sectorToEdit?: ISector | null;
  }>();

  const GRID_COLS_MIN = 8;
  const GRID_COLS_MAX = 40;
  const GRID_ROWS_MIN = 6;
  const GRID_ROWS_MAX = 30;

  const COLOR_PRESETS = [
    '#f97316',
    '#ef4444',
    '#22c55e',
    '#3b82f6',
    '#a855f7',
    '#eab308',
    '#14b8a6',
    '#737373',
  ];

  const modals = useModal();
  const ui = useUi();
  const { proxy } = getCurrentInstance()!;

  const name = ref(props.sectorToEdit?.name ?? '');
  const color = ref<string>(props.sectorToEdit?.color ?? COLOR_PRESETS[0]);
  const gridCols = ref<number>(props.sectorToEdit?.gridCols ?? 16);
  const gridRows = ref<number>(props.sectorToEdit?.gridRows ?? 10);

  const valid = ref(false);
  const saving = ref(false);

  const title = computed(() =>
    props.sectorToEdit
      ? (i18n.t('sector.modal.editTitle', {
          name: props.sectorToEdit.name,
        }) as string)
      : (i18n.t('sector.modal.newTitle') as string)
  );

  const gridColsRules = [
    (v: unknown) =>
      (Number.isInteger(Number(v)) &&
        Number(v) >= GRID_COLS_MIN &&
        Number(v) <= GRID_COLS_MAX) ||
      `${GRID_COLS_MIN} - ${GRID_COLS_MAX}`,
  ];
  const gridRowsRules = [
    (v: unknown) =>
      (Number.isInteger(Number(v)) &&
        Number(v) >= GRID_ROWS_MIN &&
        Number(v) <= GRID_ROWS_MAX) ||
      `${GRID_ROWS_MIN} - ${GRID_ROWS_MAX}`,
  ];

  function selectColor(preset: string) {
    color.value = preset;
  }

  async function save() {
    if (!valid.value || saving.value) return;

    try {
      saving.value = true;

      let sector: ISector;
      if (props.sectorToEdit) {
        sector = await SectorsService.update(props.sectorToEdit.id, {
          name: name.value.trim(),
          color: color.value,
          gridCols: Number(gridCols.value),
          gridRows: Number(gridRows.value),
        });
      } else {
        sector = await SectorsService.create({
          restaurantId: props.restaurantId,
          branchId: props.branchId,
          name: name.value.trim(),
          color: color.value,
          gridCols: Number(gridCols.value),
          gridRows: Number(gridRows.value),
        });
      }

      ui.showToast(AlertType.Success, i18n.t('sector.saved') as string);
      proxy.$parent?.$emit('saved', sector);
      // No `saving` reset on success: close() starts the modal teardown, and
      // flipping :loading during it is unsafe (see vue-renderer-safety rule).
      close();
    } catch (error) {
      // Surfaces the server-side grid shrink guard (400) among other errors.
      ui.serverError(error, i18n.t('floorPlan.sector.errorSaving') as string);
      saving.value = false;
    }
  }

  function close() {
    modals.hide('sector-modal');
  }
</script>

<template>
  <v-card class="sector-modal">
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <v-form v-model="valid" @submit.prevent="save">
        <v-text-field
          v-model="name"
          :label="$t('sector.name')"
          :hint="$t('sector.name.hint')"
          persistent-hint
          :rules="$rule('required|max64')"
          outlined
          dense
          autofocus
          class="form-field"
        />

        <div class="color-field">
          <span class="color-field-label">{{ $t('sector.color') }}</span>
          <div class="color-swatches">
            <button
              v-for="preset in COLOR_PRESETS"
              :key="preset"
              type="button"
              class="color-swatch"
              :class="{ 'is-selected': color === preset }"
              :style="{ backgroundColor: preset }"
              @click="selectColor(preset)"
            />
          </div>
        </div>

        <div class="grid-fields">
          <v-text-field
            v-model.number="gridCols"
            :label="$t('sector.gridCols')"
            type="number"
            :min="GRID_COLS_MIN"
            :max="GRID_COLS_MAX"
            :rules="gridColsRules"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            v-model.number="gridRows"
            :label="$t('sector.gridRows')"
            type="number"
            :min="GRID_ROWS_MIN"
            :max="GRID_ROWS_MAX"
            :rules="gridRowsRules"
            outlined
            dense
            hide-details="auto"
          />
        </div>
      </v-form>
    </v-card-text>
    <v-card-actions class="sector-modal-actions">
      <v-spacer />
      <v-btn text :disabled="saving" @click="close">{{ $t('cancel') }}</v-btn>
      <v-btn color="primary" :disabled="!valid" :loading="saving" @click="save">
        {{ $t('save') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .sector-modal {
    .form-field {
      @apply tw-mb-2;
    }

    .color-field {
      @apply tw-mb-4;

      .color-field-label {
        @apply tw-block tw-text-sm tw-text-gray-600 tw-mb-1.5;
      }

      .color-swatches {
        @apply tw-flex tw-flex-wrap tw-gap-2;

        .color-swatch {
          @apply tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-border-transparent tw-cursor-pointer tw-transition-transform;

          &:hover {
            @apply tw-scale-110;
          }

          &.is-selected {
            @apply tw-border-gray-800 tw-scale-110;
          }
        }
      }
    }

    .grid-fields {
      @apply tw-grid tw-grid-cols-2 tw-gap-3;

      @media (max-width: 480px) {
        @apply tw-grid-cols-1;
      }
    }

    .sector-modal-actions {
      @apply tw-px-6 tw-pb-4;
    }
  }
</style>
